import { useFeedbackContext } from "@/components/common/feedback/feedback-context";
import { useElementFileUploadContext } from "@/components/common/point-cloud-file-upload-context/element-file-upload-context";
import { loadArea } from "@/components/common/project-provider/project-loading-context";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectScansExportableDatasetSection } from "@/modes/export-mode/scans-export/use-prepare-scans-export-data";
import { selectIsPointCloudViewable } from "@/modes/select-current-area-data";

import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import {
  IElementCompleteResult,
  selectAncestorArea,
  selectIsIElementComplete,
  selectLightweightElementsSlice,
} from "@/store/lightweight-elements-selectors";
import type { LightweightElementsState } from "@/store/lightweight-elements-slice";
import { curryAppSelector } from "@/store/reselect";
import { selectActiveArea } from "@/store/selections-selectors";
import { RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  selectCanGenerateFloorplans,
  selectCanReadPointCloud,
  selectSubscriptionFeaturesSlice,
} from "@/store/subscription-features/subscription-features-selectors";
import { SubscriptionFeaturesState } from "@/store/subscription-features/subscription-features-slice";
import {
  selectHasAdminPermission,
  selectHasWritePermission,
  selectUserSlice,
} from "@/store/user-selectors";
import { UserState } from "@/store/user-slice";
import { isIElementValidPointCloudStream } from "@/types/type-guards";
import {
  addToLoadingRoots,
  grey,
  IElementsState,
  isSingleScanSection,
  removeFromLoadingRoots,
  selectAncestor,
  selectChildDepthFirst,
  selectIElement,
  selectIsVideoWalkDataSession,
  useDialog,
  useToast,
} from "@faro-lotv/app-component-toolbox";
import { assert } from "@faro-lotv/foundation";
import {
  GUID,
  IElementSection,
  isIElementAreaSection,
  isIElementGenericDataset,
  isIElementGenericImgSheet,
  isIElementImg2d,
  isIElementRoomSection,
  isIElementSectionDataSession,
  isIElementSectionDataSetCapture,
  isIElementTimeseries,
  isIElementTimeTravelSection,
  isIElementVideoRecording,
} from "@faro-lotv/ielement-types";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { Box, Divider } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import { CSSProperties, useCallback, useRef, type JSX } from "react";
import { ContextMenuSkeletonLine } from "../../lazy-faro-menu";
import { selectFromCaptureTree } from "../cad-model-tree/use-disable-capture-tree-alignment";
import {
  BaseContextMenuAction,
  ContextMenuAction,
  ContextMenuActionHandlerArgs,
} from "./action-types";
import { ACTIONS } from "./actions";
import { getSheetsToDuplicate } from "./actions/duplicate-sheet/duplicate-sheet-action";
import { selectShouldShowMarkAsAlignedAction } from "./actions/mark-sheet-as-aligned";
import { ContextMenuActionItem } from "./context-menu-action-item";
import { ContextMenuBase, ContextMenuBaseHandle } from "./context-menu-base";
import {
  selectDownloadableGaussianSplatsPly,
  selectDownloadablePointCloud,
} from "./utils";

/**
 * Invoke the proper handler for the triggered action on the selected element.
 *
 * @param action The context menu action to handle.
 * @param handlerArgs The arguments to pass to the action handler.
 * @returns a Promise to track the action's result
 */
export function contextMenuHandler(
  action: ContextMenuAction,
  handlerArgs: ContextMenuActionHandlerArgs,
): Promise<void> {
  return action.handler ? action.handler(handlerArgs) : Promise.resolve();
}

type ContextMenuProps = {
  /** ID of the tree item */
  id: GUID;

  /** Whether the node of the context menu is currently disabled. */
  isNodeDisabled: boolean;

  /**
   * Enable the dark version of the menu
   *
   * @default false
   */
  dark?: boolean;

  /** Optional color for mouse hover of the ellipsis icon used to open the menu; undefined to use the color inherited from parent */
  iconHoverColor?: CSSProperties["color"];

  /** Callback executed when the menu is opened/closed */
  onToggle?(open: boolean): void;

  /** Callback executed when an element that is in use get deleted */
  handleElementDeletion?: BaseContextMenuAction["handleElementDeletion"];
};

/**
 * @returns a context menu for the given tree item
 */
export function ContextMenu({
  id,
  isNodeDisabled,
  dark = false,
  onToggle,
  handleElementDeletion,
  iconHoverColor,
}: ContextMenuProps): JSX.Element | null {
  const visibleContextMenuActions = useAppSelector(
    selectVisibleContextMenuActions(id, isNodeDisabled),
    isEqual,
  );

  const isLightweightElement =
    useAppSelector(selectIsIElementComplete(id)) ===
    IElementCompleteResult.OnlyLightweightAvailable;

  const menuRef = useRef<ContextMenuBaseHandle>(null);

  const { getState } = useAppStore();
  const dispatch = useAppDispatch();
  const apiClients = useApiClientContext();
  const { createDialog, setConfirmButtonDisabled, confirmDialog } = useDialog();
  const { requestFeedback } = useFeedbackContext();
  const { openToast } = useToast();
  const errorHandlers = useErrorHandlers();
  const distanceUnit = useDistanceUnit();
  const elementFileUploadContext = useElementFileUploadContext();

  const onContextMenuItemClicked = useCallback(
    (elementID: GUID, action: ContextMenuAction) => {
      menuRef.current?.closeContextMenu();

      contextMenuHandler(action, {
        elementID,
        state: getState(),
        dispatch,
        apiClients,
        createDialog,
        requestFeedback,
        openToast,
        errorHandlers,
        elementFileUploadContext,
        distanceUnit,
        setConfirmButtonDisabled,
        confirmDialog,
        dark,
      }).catch((error) =>
        errorHandlers.handleErrorWithDialog({
          title: `Error: ${action.label}`,
          error,
        }),
      );
    },
    [
      getState,
      dispatch,
      apiClients,
      createDialog,
      requestFeedback,
      openToast,
      errorHandlers,
      elementFileUploadContext,
      distanceUnit,
      setConfirmButtonDisabled,
      confirmDialog,
      dark,
    ],
  );

  const onToggleMenu = useCallback(
    async (open: boolean): Promise<void> => {
      if (isLightweightElement && open) {
        // load area
        const parentArea = selectAncestorArea(id)(getState());
        if (parentArea) {
          dispatch(addToLoadingRoots(parentArea.id));
          await loadArea(parentArea.id, apiClients.projectApiClient, dispatch);
          dispatch(removeFromLoadingRoots(parentArea.id));
        }
      }
      onToggle?.(open);
    },
    [
      onToggle,
      isLightweightElement,
      getState,
      id,
      apiClients.projectApiClient,
      dispatch,
    ],
  );

  if (!isLightweightElement && !visibleContextMenuActions.length) return null;

  return (
    <ContextMenuBase
      ref={menuRef}
      onToggle={onToggleMenu}
      dark={dark}
      iconHoverColor={iconHoverColor}
    >
      {isLightweightElement && [
        <ContextMenuSkeletonLine key={1} />,
        <ContextMenuSkeletonLine key={2} />,
        <ContextMenuSkeletonLine key={3} />,
        <ContextMenuSkeletonLine key={4} />,
      ]}
      {!isLightweightElement &&
        visibleContextMenuActions.map((action, index) => {
          if (action === MenuDivider) {
            return (
              <Divider
                key={index}
                sx={{
                  mt: 1,
                  mb: 1,
                  borderColor: dark ? grey[800] : grey[200],
                }}
              />
            );
          }

          assert(typeof action !== "symbol", "Unexpected menu divider symbol");

          const actionProps = {
            action,
            elementId: id,
            onContextMenuItemClicked,
            closeContextMenu: () => menuRef.current?.closeContextMenu(),
            handleElementDeletion,
            dark,
          };

          if (action.CustomComponent) {
            // Without the Box-div component below, a click to activate the context menu action
            // would cause the tree item to be selected as well.
            return (
              <Box
                component="div"
                key={index}
                onClick={(e) => e.stopPropagation()}
              >
                <action.CustomComponent key={index} {...actionProps} />
              </Box>
            );
          }

          return <ContextMenuActionItem key={index} {...actionProps} />;
        })}
    </ContextMenuBase>
  );
}

/**
 * Returns a memoized selector to compute the context menu actions for a given ielement
 *
 * @param state The current state of the app store
 * @param id selected element id
 * @param isDisabled true if the node is disabled
 * @returns the memoized selector
 */
export const selectVisibleContextMenuActions = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectLightweightElementsSlice,
      selectUserSlice,
      selectSubscriptionFeaturesSlice,
      selectActiveArea,
      (state: RootState, id: GUID | undefined) => id,
      (state: RootState, id: GUID | undefined, isDisabled: boolean) =>
        isDisabled,
    ],
    computeVisibleContextMenuActions,
  ),
);

/**
 * @returns the context menu actions for a specific element
 * @param iElements IElements slice of the store
 * @param lightweightElements IElements slice of the store
 * @param user User slice of the store
 * @param subscriptionFeatures Subscription features slice of the store
 * @param activeArea Currently active area
 * @param id selected element id
 * @param isDisabled true if the node is disabled
 */
function computeVisibleContextMenuActions(
  iElements: IElementsState,
  lightweightElements: LightweightElementsState,
  user: UserState,
  subscriptionFeatures: SubscriptionFeaturesState,
  activeArea: IElementSection | undefined,
  id: GUID | undefined,
  isDisabled: boolean = false,
): Array<ContextMenuAction | symbol> {
  const iElementsState = { iElements };
  const iElement = selectIElement(id)(iElementsState);

  if (!id || !iElement) return [];

  const userHasWritePermission = selectHasWritePermission({ user, iElements });
  const userHasAdminPermission = selectHasAdminPermission({ user, iElements });

  if (
    isIElementSectionDataSession(iElement) ||
    isIElementGenericDataset(iElement)
  ) {
    if (selectIsVideoWalkDataSession(id)(iElementsState)) {
      return [
        ...(userHasWritePermission ? [ACTIONS.editName, MenuDivider] : []),
        ...(isDisabled ? [] : [ACTIONS.adjustTrajectory]),
        ...(userHasWritePermission ? [ACTIONS.moveDataSession] : []),
        ...(userHasAdminPermission ? [ACTIONS.downloadVideoWalk] : []),
        ...(userHasWritePermission || !isDisabled ? [MenuDivider] : []),
        ACTIONS.deleteVideoRecording,
      ];
    }

    if (!selectCanReadPointCloud({ subscriptionFeatures })) return [];

    // Check if section contains a generic pointcloud (E57, CPE, ...) of the types that allow download.
    const hasDownload =
      !!selectDownloadablePointCloud(iElement)(iElementsState);

    const hasGaussianSplatsPly =
      isIElementSectionDataSetCapture(iElement) &&
      !!selectDownloadableGaussianSplatsPly(iElement)(iElementsState);

    const hasStructuredE57 = selectScansExportableDatasetSection(iElement)({
      iElements,
      lightweightElements,
    });

    if (isDisabled) {
      return [
        ...(hasDownload ? [ACTIONS.download] : []),
        ...(hasGaussianSplatsPly ? [ACTIONS.downloadGaussianSplats] : []),
        ACTIONS.delete,
      ];
    }

    // Check if section contains a generic pointcloud stream (Potree or Webshare) which can be aligned
    const canBeAligned =
      selectChildDepthFirst(
        iElement,
        (el) =>
          isIElementValidPointCloudStream(el) &&
          selectIsPointCloudViewable(el)(iElementsState),
      )(iElementsState) !== undefined;

    const isFromCaptureTree = selectFromCaptureTree(iElement)({
      lightweightElements,
    });

    const canBeMoved =
      userHasWritePermission &&
      // Moving is only supported for data sessions outside of the capture tree
      isIElementSectionDataSession(iElement);

    const isFloorplanGenerationAvailable = selectCanGenerateFloorplans({
      subscriptionFeatures,
    });

    // Condition to show the last separator before delete action
    const showLastSeparator =
      (canBeAligned ||
        isFromCaptureTree ||
        (hasDownload && !hasStructuredE57) ||
        hasGaussianSplatsPly ||
        hasStructuredE57) ??
      isFloorplanGenerationAvailable;

    return [
      ...(userHasWritePermission ? [ACTIONS.editName] : []),
      // Structured E57 will display the date of the latest scan
      ...(userHasWritePermission && !hasStructuredE57
        ? [ACTIONS.editDate, MenuDivider]
        : []),
      ...(canBeMoved ? [ACTIONS.moveDataSession, MenuDivider] : []),
      // Enable new cloud alignment for those pointclouds that allow it.
      ...(canBeAligned ? [ACTIONS.openAlignmentWizard] : []),

      // Set a cloud as geo-referenced if it was not already
      ...(canBeAligned && !isFromCaptureTree
        ? [ACTIONS.setCloudAsGeoReferenced]
        : []),

      // Set a cloud as geo-referenced if it was not already
      ...(isFromCaptureTree ? [ACTIONS.resetCloudPosition] : []),

      // Enable download for those pointclouds that allow it.
      ...(hasDownload && !hasStructuredE57 ? [ACTIONS.download] : []),

      ...(hasGaussianSplatsPly ? [ACTIONS.downloadGaussianSplats] : []),

      // Enable the download of a structured E57 from the dataset
      ...(hasStructuredE57 ? [ACTIONS.scansExport] : []),

      // Add floorplan generation button while feature is active
      ...(isFloorplanGenerationAvailable ? [ACTIONS.generateFloorplan] : []),

      // Upload gaussian splats
      ...(isIElementSectionDataSetCapture(iElement)
        ? [ACTIONS.uploadGaussianSplats]
        : []),

      ...(showLastSeparator ? [MenuDivider] : []),

      ACTIONS.delete,
    ];
  }

  if (isIElementVideoRecording(iElement)) {
    return isDisabled
      ? [ACTIONS.deleteVideoRecording]
      : [ACTIONS.adjustTrajectory, ACTIONS.deleteVideoRecording];
  }

  if (isIElementAreaSection(iElement) || isIElementGenericImgSheet(iElement)) {
    const hasSheet =
      isIElementGenericImgSheet(iElement) ||
      selectChildDepthFirst(iElement, isIElementGenericImgSheet)({ iElements });

    // The floor scale mode needs an active area to scale
    const canScaleFloor =
      isIElementAreaSection(iElement) && hasSheet && activeArea !== undefined;

    // In case all sheets are unaligned, we give the user the option to mark one of them as aligned to use it as reference
    const canMarkAsAligned =
      isIElementGenericImgSheet(iElement) &&
      selectShouldShowMarkAsAlignedAction(iElement)(iElementsState);

    return [
      ...(isDisabled ? [] : [ACTIONS.editName, MenuDivider]),
      ...(!isDisabled &&
      userHasWritePermission &&
      isIElementAreaSection(iElement) &&
      getSheetsToDuplicate(iElement, iElementsState).length > 0
        ? [ACTIONS.duplicate, MenuDivider]
        : []),
      ...(isDisabled || !canScaleFloor ? [] : [ACTIONS.scaleFloor]),
      ...(isDisabled || !hasSheet ? [] : [ACTIONS.openAlignmentWizard]),
      ...(isDisabled || !canMarkAsAligned ? [] : [ACTIONS.markSheetAsAligned]),
      ...[ACTIONS.delete],
    ];
  }

  if (
    userHasWritePermission &&
    // This selects sections for entire rooms and the sections for individual time points.
    // The data structure is not ideal, because both types of section can have the "room" type hint.
    (isIElementRoomSection(iElement) || isIElementTimeTravelSection(iElement))
  ) {
    // room sections can be edited only on top level, but not in case if they are children of timeseries
    const timeSeries = selectAncestor(
      iElement,
      isIElementTimeseries,
    )(iElementsState);

    if (timeSeries) {
      return [
        // Actions for a single time point
        ACTIONS.editElement,
        ACTIONS.moveTimePoint,
        ACTIONS.assignTags,
        ACTIONS.downloadImage,
        ACTIONS.delete,
        // use MenuDivider to add a separator before "Add data"
        MenuDivider,
        ACTIONS.addData,
      ];
    }

    return [
      // Actions for an entire room section
      ACTIONS.editWaypointName,
      MenuDivider,
      ACTIONS.enableEditWaypoints,
      ACTIONS.moveWaypointToOtherArea,
      ACTIONS.copyAsLocationMarker,
      MenuDivider,
      ACTIONS.assignTags,
      MenuDivider,
      ACTIONS.editWaypointColor,
      MenuDivider,
      ACTIONS.addData,
      MenuDivider,
      ACTIONS.delete,
    ];
  }

  if (userHasWritePermission && isSingleScanSection(iElement)) {
    return [ACTIONS.assignTags];
  }

  if (userHasWritePermission && isIElementImg2d(iElement)) {
    return [
      // Actions for a 2d photo
      ACTIONS.downloadImage,
      ACTIONS.delete,
      // use null to add a separator before "Add data"
      MenuDivider,
      ACTIONS.addData,
    ];
  }

  return [];
}

export const MenuDivider = Symbol("MenuDivider");
